"use client";

import { QueryClient, QueryClientProvider, QueryClientConfig } from "@tanstack/react-query";
import { ReactNode, useState, useEffect } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

const defaultConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
};

interface QueryProviderProps {
  children: ReactNode;
  config?: QueryClientConfig;
}

export function QueryProvider({ children, config = defaultConfig }: QueryProviderProps) {
  const [queryClient] = useState(() => new QueryClient(config));

  useEffect(() => {
    const sessionPersister = createSyncStoragePersister({
      storage: typeof window !== "undefined" ? window.sessionStorage : undefined,
    });

    persistQueryClient({
      queryClient,
      persister: sessionPersister,
      maxAge: 1000 * 60 * 10, // 10 minutes, matching staleTime
    });
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
